import { z, ZodError } from "zod";
import { SwayyProductImageType } from "../models/images/SwayyProductImage";
import { SwayyProductInfoFormValues } from "../models/products/SwayyProductForm";

/** Align with swayy-backend catalog admin Zod schemas (form field names). */

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** images.types.ts — upload limits & MIME types */
export const MAX_PRODUCT_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024;
export const MAX_PRODUCT_IMAGE_UPLOAD_FILES = 30;
export const ALLOWED_PRODUCT_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
] as const;

const allowedImageMime = new Set<string>(ALLOWED_PRODUCT_IMAGE_MIME_TYPES);

export const productImageTypeSchema = z.union([
  z.literal("portrait"),
  z.literal("landscape"),
  z.literal("square"),
]);

/** createImageSchema / updateImageSchema field constraints (client form names). */
export const updateImageMetaFormSchema = z.object({
  alt: z.string().trim().max(255, "Alt text must be at most 255 characters."),
  imageType: productImageTypeSchema,
});

export const imageUploadFilesSchema = z
  .array(
    z.custom<File>((value): value is File => value instanceof File, {
      message: "Invalid file.",
    }),
  )
  .min(1, "Please choose at least one image.")
  .max(
    MAX_PRODUCT_IMAGE_UPLOAD_FILES,
    `You can upload at most ${MAX_PRODUCT_IMAGE_UPLOAD_FILES} images at once.`,
  )
  .superRefine((files, ctx) => {
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      if (!allowedImageMime.has(file.type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Only JPEG, PNG, and WebP images are allowed.",
          path: [index],
        });
        return;
      }
      if (file.size > MAX_PRODUCT_IMAGE_UPLOAD_BYTES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each image must be 2MB or smaller.",
          path: [index],
        });
        return;
      }
    }
  });

const taxRateSchema = z
  .number()
  .min(0, "Tax rate must be between 0 and 100.")
  .max(100, "Tax rate must be between 0 and 100.")
  .nullable();

const optionalNumberField = (label: string) =>
  z
    .string()
    .refine(
      (value) => value.trim() === "" || Number.isFinite(Number(value)),
      `${label} must be a number.`,
    );

const productAttributeFieldsSchema = {
  priority: optionalNumberField("Priority"),
  length: optionalNumberField("Length"),
  breadth: optionalNumberField("Breadth"),
  height: optionalNumberField("Height"),
  weight: optionalNumberField("Weight"),
};

export const createCategoryFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(160),
  skuCode: z.string().trim().min(1, "SKU code is required.").max(12),
});

export const createOptionFormSchema = z.object({
  name: z.string().trim().min(1, "Option name is required.").max(80),
  displayType: z.union([
    z.literal("dropdown"),
    z.literal("swatch"),
    z.literal("button"),
    z.literal("text"),
  ]),
});

export const createPriceListFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(120),
  type: z.union([z.literal("b2c"), z.literal("b2b")]),
  currency: z
    .string()
    .trim()
    .length(3, "Currency must be a 3-letter code.")
    .transform((value) => value.toUpperCase()),
  isDefault: z.boolean(),
});

export const updatePriceListFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(120),
  currency: z
    .string()
    .trim()
    .length(3, "Currency must be a 3-letter code.")
    .transform((value) => value.toUpperCase()),
  isDefault: z.boolean(),
  isActive: z.boolean(),
});

/** Align with swayy-backend upsertPricesSchema (MRP / sale_price). */
export const variantPriceDraftSchema = z
  .object({
    mrp: z.coerce
      .number({ invalid_type_error: "MRP must be greater than 0." })
      .positive("MRP must be greater than 0."),
    salePrice: z.coerce
      .number({ invalid_type_error: "Sale price must be greater than 0." })
      .positive("Sale price must be greater than 0."),
  })
  .refine((data) => data.salePrice <= data.mrp, {
    message: "Sale price must be ≤ MRP.",
    path: ["salePrice"],
  });

export const addOptionValueFormSchema = z.object({
  value: z.string().trim().min(1, "Value is required.").max(120),
  skuCode: z.string().trim().min(1, "SKU code is required.").max(12),
  hex: z.string().trim(),
});

export const HEX_INVALID_MESSAGE = "Hex must look like #FFF or #FFFFFF.";

export const validateOptionValueHex = (
  hex: string,
): { ok: true; hex: string | null } | { ok: false; message: string } => {
  const trimmed = hex.trim();
  if (!trimmed) return { ok: true, hex: null };
  if (!HEX_COLOR.test(trimmed)) {
    return { ok: false, message: HEX_INVALID_MESSAGE };
  }
  return { ok: true, hex: trimmed };
};

export const productCreateFormSchema = z.object({
  name: z.string().trim().min(1, "Product name is required.").max(255),
  slug: z.string().trim().max(280),
  description: z.string(),
  brandId: z.number().int().positive().nullable(),
  primaryCategoryId: z
    .number()
    .int()
    .positive("Primary category is required.")
    .nullable()
    .refine((value): value is number => value !== null, {
      message: "Primary category is required.",
    }),
  additionalCategoryIds: z.array(z.number().int().positive()),
  hsnCode: z.string().trim().max(12, "HSN code must be at most 12 characters."),
  taxRate: taxRateSchema,
  isB2c: z.boolean(),
  isB2b: z.boolean(),
  ...productAttributeFieldsSchema,
});

/** Update: only constraints that always apply client-side. */
export const productUpdateClientSchema = z.object({
  hsnCode: z.string().trim().max(12, "HSN code must be at most 12 characters."),
  taxRate: taxRateSchema,
  ...productAttributeFieldsSchema,
});

export const zodFirstMessage = (error: ZodError): string =>
  error.issues[0]?.message || "Validation failed.";

export const zodFieldErrors = <K extends string>(
  error: ZodError,
): Partial<Record<K, string>> => {
  const errors: Partial<Record<K, string>> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] || "") as K;
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return errors;
};

export const validateImageUploadFiles = (
  files: File[],
): { ok: true } | { ok: false; message: string } => {
  const result = imageUploadFilesSchema.safeParse(files);
  if (result.success) return { ok: true };
  return { ok: false, message: zodFirstMessage(result.error) };
};

export const validateUpdateImageMetaForm = (form: {
  alt: string;
  imageType: SwayyProductImageType;
}): { ok: true } | { ok: false; message: string } => {
  const result = updateImageMetaFormSchema.safeParse(form);
  if (result.success) return { ok: true };
  return { ok: false, message: zodFirstMessage(result.error) };
};

export const validateVariantPriceDraft = (draft: {
  mrp: string;
  salePrice: string;
}):
  | { ok: true; mrp: number; salePrice: number }
  | { ok: false; message: string } => {
  const result = variantPriceDraftSchema.safeParse({
    mrp: draft.mrp.trim() === "" ? NaN : Number(draft.mrp),
    salePrice: draft.salePrice.trim() === "" ? NaN : Number(draft.salePrice),
  });
  if (!result.success) {
    return { ok: false, message: zodFirstMessage(result.error) };
  }
  return {
    ok: true,
    mrp: result.data.mrp,
    salePrice: result.data.salePrice,
  };
};

export const validateProductForm = (
  form: SwayyProductInfoFormValues,
  options: { requireRequiredFields: boolean },
): Partial<Record<keyof SwayyProductInfoFormValues, string>> => {
  const schema = options.requireRequiredFields
    ? productCreateFormSchema
    : productUpdateClientSchema;
  const result = schema.safeParse(form);
  if (result.success) return {};
  return zodFieldErrors(result.error);
};

export const validateAddOptionValueForm = (
  form: { value: string; skuCode: string; hex: string },
  isSwatch: boolean,
): { ok: true } | { ok: false; message: string } => {
  const result = addOptionValueFormSchema.safeParse(form);
  if (!result.success) {
    return { ok: false, message: zodFirstMessage(result.error) };
  }
  if (isSwatch) {
    const hexResult = validateOptionValueHex(form.hex);
    if (!hexResult.ok) return hexResult;
  }
  return { ok: true };
};
